/**
 * Monster Onesie — cursor-tracking googly eyes.
 *
 * Used by the footer lockup (BRIEF §3.3) and the email panel (§4.6). Both
 * wrap an inline SVG in <monster-googly>; every <circle class="pupil">
 * inside it tracks the pointer, offset by up to `data-r` user units.
 *
 * One shared rAF loop across every instance on the page, started only while
 * at least one instance is on screen, so a footer mark 4000px down costs
 * nothing until it is scrolled to.
 *
 * No external libraries (§1). Honours prefers-reduced-motion: the eyes
 * render, they just don't follow.
 */

/** @type {Set<MonsterGoogly>} */
const active = new Set();

/** @type {number | null} */
let rafId = null;

let pointerX = 0;
let pointerY = 0;
let pointerSeen = false;

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

function onPointerMove(event) {
  pointerX = event.clientX;
  pointerY = event.clientY;
  pointerSeen = true;
}

function tick() {
  // Nothing to look at until the pointer has actually moved: without this the
  // eyes would snap to 0,0 (the top-left corner) on load, which reads as a
  // rendering bug rather than a character.
  if (pointerSeen) {
    for (const instance of active) instance.track(pointerX, pointerY);
  }
  rafId = active.size > 0 ? requestAnimationFrame(tick) : null;
}

function start() {
  if (rafId === null && active.size > 0) rafId = requestAnimationFrame(tick);
}

const visibility = new IntersectionObserver((entries) => {
  for (const entry of entries) {
    const instance = entry.target;
    if (!(instance instanceof MonsterGoogly)) continue;

    if (entry.isIntersecting) {
      active.add(instance);
    } else {
      active.delete(instance);
      instance.rest();
    }
  }
  start();
});

class MonsterGoogly extends HTMLElement {
  /** @type {SVGCircleElement[]} */
  #pupils = [];

  connectedCallback() {
    this.#pupils = Array.from(this.querySelectorAll('.pupil'));
    if (this.#pupils.length === 0) return;

    if (reducedMotion.matches) return;

    window.addEventListener('pointermove', onPointerMove, { passive: true });
    visibility.observe(this);
  }

  disconnectedCallback() {
    visibility.unobserve(this);
    active.delete(this);
  }

  /** Point every pupil at the cursor. */
  track(x, y) {
    for (const pupil of this.#pupils) {
      const svg = pupil.ownerSVGElement;
      if (!svg) continue;

      const box = svg.getBoundingClientRect();
      if (box.width === 0) continue;

      // cx/cy are in the 0-100 viewBox, so scale them into page space.
      const cx = Number(pupil.getAttribute('cx'));
      const cy = Number(pupil.getAttribute('cy'));
      const eyeX = box.left + box.width * (cx / 100);
      const eyeY = box.top + box.height * (cy / 100);

      const angle = Math.atan2(y - eyeY, x - eyeX);
      // Travel maxes out at 260px away, so a cursor on the far side of the
      // screen doesn't look any more extreme than one just out of reach.
      const distance = Math.min(1, Math.hypot(x - eyeX, y - eyeY) / 260);
      const reach = Number(pupil.dataset.r) || 5;

      pupil.setAttribute(
        'transform',
        `translate(${Math.cos(angle) * reach * distance},${Math.sin(angle) * reach * distance})`
      );
    }
  }

  /** Recentre the pupils when scrolled out of view. */
  rest() {
    for (const pupil of this.#pupils) pupil.removeAttribute('transform');
  }
}

if (!customElements.get('monster-googly')) {
  customElements.define('monster-googly', MonsterGoogly);
}
